<template>
  <AdminShell
    :page-title="pageTitle"
    :page-subtitle="pageSubtitle"
    active-menu="home"
  >
    <section class="home-dashboard">
      <div v-if="loading" class="home-dashboard__feedback">加载中...</div>

      <div v-else-if="cards.length === 0" class="home-dashboard__empty">
        <div class="home-dashboard__empty-title">暂无直播场次</div>
        <div class="home-dashboard__empty-text">当前没有正在直播的场次，可直接创建直播间并生成 OBS 推流码。</div>
        <button class="home-dashboard__empty-action" type="button" @click="openCreateObsDialog">
          创建直播并生成推流码
        </button>
      </div>

      <template v-else>
        <div v-if="!hasLiveRoom" class="home-dashboard__no-live-tip">
          <div>
            <strong>{{ hasIdleRoom ? '存在尚未开始的直播间' : '当前没有正在直播的场次' }}</strong>
            <span>
              {{ hasIdleRoom
                ? '请在“未开始”卡片中生成 OBS 推流码，系统会自动补齐直播状态。'
                : '创建新直播间后可立即获得 OBS 服务器地址和串流密钥。' }}
            </span>
          </div>
          <button v-if="!hasIdleRoom" type="button" @click="openCreateObsDialog">创建并生成推流码</button>
        </div>

        <div class="home-dashboard__grid">
        <article
          v-for="room in cards"
          :key="room.stream_name"
          class="home-dashboard__card"
        >
          <div class="home-dashboard__card-cover">
            <img :src="room.cover" :alt="room.displayTitle" class="home-dashboard__card-image" />
            <span
              class="home-dashboard__card-badge"
              :class="`is-${room.statusKey}`"
            >
              {{ room.statusText }}
            </span>

            <div class="home-dashboard__card-toolbar">
              <button
                class="home-dashboard__watch-button"
                type="button"
                @click="goToWatch(room)"
              >
                <i class="el-icon-link"></i>
                <span>查看直播</span>
              </button>
              <button
                class="home-dashboard__qr-button"
                type="button"
                :disabled="!qrUrl"
                @click="openQr(room)"
              >
                <i class="el-icon-s-grid"></i>
              </button>
            </div>
          </div>

          <div class="home-dashboard__card-body">
            <h3 class="home-dashboard__card-title">{{ room.displayTitle }}</h3>

            <div class="home-dashboard__meta">
              <div class="home-dashboard__meta-row">
                <span class="home-dashboard__meta-label">
                  <i class="el-icon-time"></i>
                  开始时间
                </span>
                <span class="home-dashboard__meta-value">{{ room.displayTime }}</span>
              </div>
              <div class="home-dashboard__meta-row">
                <span class="home-dashboard__meta-label">
                  <i class="el-icon-user"></i>
                  观看人数
                </span>
                <span class="home-dashboard__meta-value">{{ room.viewerText }}</span>
              </div>
            </div>

            <button
              class="home-dashboard__report-button"
              type="button"
              @click="goToReport(room)"
            >
              查看详细报告
            </button>

            <div
              v-if="room.statusKey === 'live' || room.statusKey === 'idle'"
              class="home-dashboard__live-actions"
              :class="{ 'is-single': room.statusKey === 'idle' }"
            >
              <button
                class="home-dashboard__obs-button"
                type="button"
                :disabled="!!obsLoadingByRoom[room.stream_name] || !!closingByRoom[room.stream_name]"
                @click="generateObsForLiveRoom(room)"
              >
                <i class="el-icon-video-camera"></i>
                {{ obsLoadingByRoom[room.stream_name]
                  ? '正在生成...'
                  : (room.statusKey === 'idle' ? '启用并生成 OBS 推流码' : '生成 OBS 推流码') }}
              </button>
              <button
                v-if="room.statusKey === 'live'"
                class="home-dashboard__close-button"
                type="button"
                :disabled="!!closingByRoom[room.stream_name] || !!obsLoadingByRoom[room.stream_name]"
                @click="closeLiveRoom(room)"
              >
                <i class="el-icon-switch-button"></i>
                {{ closingByRoom[room.stream_name] ? '关播中...' : '关播' }}
              </button>
            </div>
          </div>
        </article>
        </div>
      </template>
    </section>

    <QrCodeDialog
      :visible.sync="showQr"
      :title="qrDialogTitle"
      :url="qrTargetUrl"
      :size="320"
      :file-name="qrDialogFileName"
    />

    <el-dialog
      title="创建直播并生成 OBS 推流码"
      :visible.sync="showCreateObs"
      width="520px"
      :close-on-click-modal="!creatingRoom"
      :close-on-press-escape="!creatingRoom"
      :show-close="!creatingRoom"
      custom-class="home-dashboard__dialog"
    >
      <div class="home-dashboard__create-form">
        <p>系统将创建一个直播间、登记固定主播 UID，并立即生成 OBS 推流参数。</p>
        <label for="obs-live-title">直播标题</label>
        <el-input
          id="obs-live-title"
          v-model.trim="newLiveTitle"
          maxlength="60"
          show-word-limit
          placeholder="请输入直播标题"
          :disabled="creatingRoom"
          @keyup.enter.native="createRoomAndObs"
        />
        <div class="home-dashboard__safety-note">
          新建流程不会启动 SDK 推流。生成后请只使用 OBS 推流，避免相同频道和 UID 被两个推流源同时占用。
        </div>
      </div>
      <template #footer>
        <el-button :disabled="creatingRoom" @click="showCreateObs = false">取消</el-button>
        <el-button type="primary" :loading="creatingRoom" @click="createRoomAndObs">
          创建并生成
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      title="OBS 推流参数"
      :visible.sync="showObsCredentials"
      width="620px"
      custom-class="home-dashboard__dialog"
      @closed="clearObsCredentials"
    >
      <div v-if="obsCredentials" class="home-dashboard__credentials">
        <div class="home-dashboard__credential-warning">
          串流密钥仅在本次生成时完整显示。请立即复制并妥善保管，不要发送给无关人员。
        </div>

        <div class="home-dashboard__credential-row">
          <span>OBS 服务</span>
          <div>
            <code>{{ obsCredentials.server }}</code>
            <el-button size="mini" @click="copyText(obsCredentials.server, '服务器地址')">复制</el-button>
          </div>
        </div>
        <div class="home-dashboard__credential-row">
          <span>串流密钥</span>
          <div>
            <code>{{ obsCredentials.streamKey }}</code>
            <el-button size="mini" type="primary" @click="copyText(obsCredentials.streamKey, '串流密钥')">复制</el-button>
          </div>
        </div>
        <div class="home-dashboard__credential-meta">
          <span>频道：{{ obsCredentials.channel || obsCredentials.roomId || '--' }}</span>
          <span>主播 UID：{{ obsCredentials.uid || '--' }}</span>
          <span>有效期至：{{ formatCredentialExpiry(obsCredentials.expiresAt) }}</span>
        </div>

        <ol class="home-dashboard__obs-steps">
          <li>打开 OBS 设置 → 推流，将“服务”设为自定义。</li>
          <li>粘贴上方服务器地址和串流密钥并开始推流。</li>
          <li>不要同时使用 APP 或网页 SDK 在该直播间推流。</li>
        </ol>
      </div>
      <template #footer>
        <el-button type="primary" @click="showObsCredentials = false">我已保存</el-button>
      </template>
    </el-dialog>
  </AdminShell>
</template>

<script>
import {
  changeLiveStatus,
  createObsCredentials,
  createStream,
  getAppid,
  liveroomsList,
  registerObsPublisherIdentity,
} from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import QrCodeDialog from '@/components/QrCodeDialog.vue'

const PAGE_TITLE = '\u76f4\u64ad\u573a\u6b21\u6570\u636e'
const PAGE_SUBTITLE = '\u7ba1\u7406\u5e76\u76d1\u63a7\u60a8\u6700\u8fd1\u7684\u76f4\u64ad\u52a8\u6001'

export default {
  name: 'Home',
  components: {
    AdminShell,
    QrCodeDialog,
  },
  data() {
    return {
      liveRooms: [],
      loading: false,
      appid: 0,
      pageTitle: PAGE_TITLE,
      pageSubtitle: PAGE_SUBTITLE,
      qrUrl: '',
      showQr: false,
      activeQrRoom: null,
      pageSize: 60,
      showCreateObs: false,
      newLiveTitle: '',
      creatingRoom: false,
      obsLoadingByRoom: {},
      closingByRoom: {},
      obsRequestKeyByRoom: {},
      showObsCredentials: false,
      obsCredentials: null,
    }
  },
  computed: {
    hasLiveRoom() {
      return this.liveRooms.some((room) => Number(room.status) === 1)
    },
    hasIdleRoom() {
      return this.liveRooms.some((room) => Number(room.status) === 0)
    },
    cards() {
      return this.liveRooms.map((roomInfo) => {
        const status = Number(roomInfo.status)
        const viewerCount = this.resolveViewerCount(roomInfo)
        return {
          ...roomInfo,
          // cover: this.resolveCover(roomInfo),
          cover: '',
          displayTitle: roomInfo.live_title || roomInfo.stream_name || '未命名直播间',
          displayTime: this.formatDisplayTime(roomInfo.time_on || roomInfo.created_at),
          statusText: this.getStatusText(status),
          statusKey: this.getStatusKey(status),
          viewerText: Number(viewerCount || 0).toLocaleString(),
        }
      })
    },
    qrDialogTitle() {
      const room = this.activeQrRoom
      if (!room) return '直播间二维码'
      return `${room.live_title || room.stream_name || '直播间'}二维码`
    },
    qrDialogFileName() {
      const room = this.activeQrRoom
      if (!room || !room.stream_name) return 'wokfoy-live_qrcode'
      return `wokfoy-live_${room.stream_name}`
    },
    qrTargetUrl() {
      const room = this.activeQrRoom
      if (!room || !this.qrUrl) return this.qrUrl
      const base = /^https?:\/\//i.test(this.qrUrl) ? this.qrUrl : `https://${this.qrUrl}`
      const cleanBase = base.replace(/\/+$/, '')
      const separator = cleanBase.includes('?') ? '&' : '?'
      return `${cleanBase}${separator}room=${encodeURIComponent(room.stream_name || '')}`
    },
  },
  async mounted() {
    await this.initPage()
  },
  methods: {
    async initPage() {
      this.loading = true
      try {
        const { data: apps } = await getAppid()
        const currentApp = Array.isArray(apps) ? apps[0] : null
        if (!currentApp) return

        this.qrUrl = currentApp.live_domain || ''
        this.appid = currentApp.id

        await this.loadRooms()
      } finally {
        this.loading = false
      }
    },
    async loadRooms() {
      if (!this.appid) return
      const { data } = await liveroomsList(this.appid, this.pageSize, 1)
      this.liveRooms = Array.isArray(data.data) ? data.data : []
    },
    getStatusText(status) {
      if (status === 1) return '正在直播'
      if (status === 2) return '已结束'
      return '未开始'
    },
    getStatusKey(status) {
      if (status === 1) return 'live'
      if (status === 2) return 'ended'
      return 'idle'
    },
    resolveViewerCount(roomInfo) {
      const candidates = [roomInfo.online_count, roomInfo.viewer_count, roomInfo.visit_count, 0]
      const value = candidates.find((item) => item !== undefined && item !== null && item !== '')
      const numeric = Number(value)
      return Number.isFinite(numeric) ? numeric : 0
    },
    resolveCover(roomInfo) {
      return [roomInfo.image, roomInfo.cover, roomInfo.poster, roomInfo.thumb, '']
        .find((item) => item !== undefined && item !== null && item !== '')
    },
    formatDisplayTime(value) {
      if (value === null || value === undefined || value === '') return '--'

      const raw = String(value).trim()
      const numeric = Number(raw)
      let date = null

      if (raw && Number.isFinite(numeric)) {
        date = new Date(raw.length === 10 ? numeric * 1000 : numeric)
      } else {
        date = new Date(raw.replace(/-/g, '/'))
      }

      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return raw
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    openCreateObsDialog() {
      if (this.creatingRoom) return
      this.newLiveTitle = this.newLiveTitle || `OBS 直播 ${this.formatApiDateTime(new Date()).slice(0, 16)}`
      this.showCreateObs = true
    },
    async createRoomAndObs() {
      const title = String(this.newLiveTitle || '').trim()
      if (!title) {
        this.$message.warning('请输入直播标题')
        return
      }
      if (!this.appid || this.creatingRoom) return

      this.creatingRoom = true
      let createdRoom = null
      try {
        await this.loadRooms()
        if (this.hasLiveRoom) {
          this.showCreateObs = false
          this.$message.warning('已有正在直播的场次，请直接在该直播卡片中生成 OBS 推流码')
          return
        }

        const now = new Date()
        const endAt = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)
        const { data } = await createStream({
          appid: this.appid,
          live_title: title,
          time_on: this.formatApiDateTime(now),
          time_out: this.formatApiDateTime(endAt),
        })
        createdRoom = data || {}
        const roomId = String(createdRoom.stream_name || '').trim()
        if (!roomId) throw new Error('创建直播成功，但响应中缺少直播间频道')

        await registerObsPublisherIdentity(roomId, this.createRequestId('rtc'))
        await changeLiveStatus({
          appid: this.appid,
          stream_name: roomId,
          stream_status: 1,
        })

        const credential = await this.requestObsCredentials(roomId)
        this.showCreateObs = false
        this.presentObsCredentials(credential)
        this.$message.success('直播间和 OBS 推流码已生成')
      } catch (error) {
        const roomId = String(createdRoom?.stream_name || '').trim()
        if (roomId) {
          this.showCreateObs = false
          this.$alert(
            `直播间已创建（${roomId}），但 OBS 推流码生成失败：${error.message || '未知错误'}。请在直播卡片中重试生成。`,
            '部分完成',
            { type: 'warning' }
          ).catch(() => {})
        } else {
          this.$message.error(error.message || '创建直播间失败')
        }
      } finally {
        this.creatingRoom = false
        await this.loadRooms().catch(() => {})
      }
    },
    async generateObsForLiveRoom(room) {
      const roomId = String(room?.stream_name || '').trim()
      if (!roomId || this.obsLoadingByRoom[roomId]) return
      const isIdleRoom = Number(room?.status) === 0

      try {
        await this.$confirm(
          isIdleRoom
            ? '该直播间当前为“未开始”。继续后系统会登记固定主播 UID、将状态更新为直播中，并生成 OBS 推流码。'
            : '请确认 APP 或网页 SDK 已停止发布并离开该直播间。确认后才能由 OBS 使用相同频道和主播 UID 接管推流。',
          isIdleRoom ? '启用直播间并生成 OBS 推流码' : '生成 OBS 推流码',
          {
            confirmButtonText: isIdleRoom ? '启用并生成' : '已停止 SDK，继续生成',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
      } catch (error) {
        return
      }

      this.$set(this.obsLoadingByRoom, roomId, true)
      try {
        if (isIdleRoom) {
          await registerObsPublisherIdentity(roomId, this.createRequestId('rtc'))
          await changeLiveStatus({
            appid: this.appid,
            stream_name: roomId,
            stream_status: 1,
          })
        }
        const credential = await this.requestObsCredentials(roomId)
        this.presentObsCredentials(credential)
        this.$message.success(isIdleRoom ? '直播间已启用，OBS 推流码已生成' : 'OBS 推流码已生成')
      } catch (error) {
        this.$message.error(error.message || (isIdleRoom ? '启用直播间或生成 OBS 推流码失败' : 'OBS 推流码生成失败'))
      } finally {
        this.$delete(this.obsLoadingByRoom, roomId)
        if (isIdleRoom) await this.loadRooms().catch(() => {})
      }
    },
    async closeLiveRoom(room) {
      const roomId = String(room?.stream_name || '').trim()
      if (!roomId || this.closingByRoom[roomId]) return

      try {
        await this.$confirm(
          `确定结束“${room.displayTitle || roomId}”吗？请先在 OBS、APP 或网页推流工具中停止推流。关播后，该场次将标记为已结束。`,
          '确认关播',
          {
            confirmButtonText: '确认关播',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
      } catch (error) {
        return
      }

      this.$set(this.closingByRoom, roomId, true)
      try {
        await changeLiveStatus({
          appid: this.appid,
          stream_name: roomId,
          stream_status: 2,
        })
        this.$delete(this.obsRequestKeyByRoom, roomId)
        await this.loadRooms()
        this.$message.success('直播已结束')
      } catch (error) {
        this.$message.error(error.message || '关播失败')
      } finally {
        this.$delete(this.closingByRoom, roomId)
      }
    },
    async requestObsCredentials(roomId) {
      const idempotencyKey = this.obsRequestKeyByRoom[roomId] || this.createRequestId('obs')
      this.$set(this.obsRequestKeyByRoom, roomId, idempotencyKey)
      const response = await createObsCredentials(roomId, idempotencyKey)
      const credential = response?.data
      if (!credential?.server || !credential?.streamKey) {
        throw new Error('OBS 凭证响应缺少服务器地址或串流密钥')
      }
      this.$delete(this.obsRequestKeyByRoom, roomId)
      return credential
    },
    presentObsCredentials(credential) {
      this.obsCredentials = credential
      this.showObsCredentials = true
    },
    clearObsCredentials() {
      this.obsCredentials = null
    },
    createRequestId(prefix) {
      const uuid = window.crypto?.randomUUID?.()
      if (uuid) return `${prefix}-${uuid}`
      const random = Math.random().toString(36).slice(2)
      return `${prefix}-${Date.now()}-${random}`
    },
    formatApiDateTime(value) {
      const date = value instanceof Date ? value : new Date(value)
      const pad = (number) => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    formatCredentialExpiry(value) {
      if (!value) return '--'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('zh-CN', { hour12: false })
    },
    async copyText(value, label) {
      const text = String(value || '')
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
      } catch (error) {
        const input = document.createElement('textarea')
        input.value = text
        input.style.position = 'fixed'
        input.style.opacity = '0'
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }
      this.$message.success(`${label}已复制`)
    },
    goToWatch(roomInfo) {
      this.$router.push({
        path: '/BigScreen',
        query: {
          room: roomInfo.stream_name || '',
        },
      })
    },
    goToReport(roomInfo) {
      this.$router.push({
        path: '/live-overview',
        query: {
          room_id: roomInfo.stream_name || '',
        },
      })
    },
    openQr(roomInfo) {
      this.activeQrRoom = roomInfo
      this.showQr = true
    },
  },
}
</script>

<style scoped>
.home-dashboard {
  min-height: 420px;
}

.home-dashboard__feedback,
.home-dashboard__empty {
  min-height: 360px;
  display: grid;
  place-items: center;
  border: 1px solid #dde4f2;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.72);
  color: #667792;
}

.home-dashboard__empty {
  place-items: center;
  align-content: center;
  gap: 10px;
}

.home-dashboard__empty-title {
  color: #12203f;
  font-size: 22px;
  font-weight: 700;
}

.home-dashboard__empty-text {
  font-size: 14px;
}

.home-dashboard__empty-action,
.home-dashboard__no-live-tip button {
  min-height: 42px;
  padding: 0 20px;
  border: 0;
  border-radius: 12px;
  background: #315efb;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.home-dashboard__no-live-tip {
  margin-bottom: 22px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 1px solid #cdd9ff;
  border-radius: 18px;
  background: #f3f6ff;
  color: #12203f;
}

.home-dashboard__no-live-tip div,
.home-dashboard__no-live-tip span {
  display: block;
}

.home-dashboard__no-live-tip span {
  margin-top: 5px;
  color: #667792;
  font-size: 14px;
}

.home-dashboard__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}

.home-dashboard__card {
  overflow: hidden;
  border: 1px solid #dbe2ee;
  border-radius: 26px;
  background: #fff;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.06);
}

.home-dashboard__card-cover {
  position: relative;
  height: 240px;
  background: #0f172a;
}

.home-dashboard__card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.home-dashboard__card-badge {
  position: absolute;
  top: 18px;
  left: 22px;
  min-width: 78px;
  height: 32px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.home-dashboard__card-badge.is-live {
  background: #ff5451;
}

.home-dashboard__card-badge.is-idle {
  background: #4689ff;
}

.home-dashboard__card-badge.is-ended {
  background: #6e7e99;
}

.home-dashboard__card-toolbar {
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.home-dashboard__watch-button,
.home-dashboard__qr-button,
.home-dashboard__report-button,
.home-dashboard__obs-button,
.home-dashboard__close-button {
  border: 1px solid #dbe2ee;
  background: #fff;
  color: #12203f;
  cursor: pointer;
}

.home-dashboard__watch-button {
  flex: 1;
  min-height: 48px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
}

.home-dashboard__qr-button {
  width: 52px;
  height: 48px;
  border-radius: 14px;
  font-size: 18px;
}

.home-dashboard__qr-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.home-dashboard__card-body {
  padding: 24px 24px 26px;
}

.home-dashboard__card-title {
  min-height: 68px;
  margin: 0;
  color: #12203f;
  font-size: 22px;
  line-height: 1.55;
  font-weight: 800;
}

.home-dashboard__meta {
  margin-top: 14px;
  display: grid;
  gap: 10px;
}

.home-dashboard__meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #64748b;
  font-size: 14px;
}

.home-dashboard__meta-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.home-dashboard__meta-value {
  color: #12203f;
  font-size: 16px;
}

.home-dashboard__report-button {
  width: 100%;
  min-height: 54px;
  margin-top: 24px;
  border-radius: 14px;
  font-size: 16px;
}

.home-dashboard__live-actions {
  margin-top: 12px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px;
  gap: 10px;
}

.home-dashboard__live-actions.is-single {
  grid-template-columns: 1fr;
}

.home-dashboard__obs-button,
.home-dashboard__close-button {
  min-height: 54px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
}

.home-dashboard__obs-button {
  border-color: #315efb;
  color: #315efb;
}

.home-dashboard__close-button {
  border-color: #f2b8b5;
  background: #fff7f6;
  color: #d9342b;
}

.home-dashboard__obs-button:disabled,
.home-dashboard__close-button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.home-dashboard__create-form p {
  margin: 0 0 20px;
  color: #52637f;
  line-height: 1.7;
}

.home-dashboard__create-form label {
  display: block;
  margin-bottom: 8px;
  color: #12203f;
  font-weight: 700;
}

.home-dashboard__safety-note,
.home-dashboard__credential-warning {
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff7e8;
  color: #9a5b00;
  font-size: 13px;
  line-height: 1.65;
}

.home-dashboard__credential-warning {
  margin-top: 0;
  margin-bottom: 20px;
}

.home-dashboard__credential-row {
  padding: 14px 0;
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  border-bottom: 1px solid #e6ebf3;
  color: #52637f;
}

.home-dashboard__credential-row > div {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.home-dashboard__credential-row code {
  min-width: 0;
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f4f6fa;
  color: #12203f;
  word-break: break-all;
}

.home-dashboard__credential-meta {
  margin-top: 16px;
  display: grid;
  gap: 6px;
  color: #667792;
  font-size: 13px;
}

.home-dashboard__obs-steps {
  margin: 20px 0 0;
  padding-left: 22px;
  color: #33415c;
  line-height: 1.8;
}

@media (max-width: 1280px) {
  .home-dashboard__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .home-dashboard__no-live-tip {
    align-items: stretch;
    flex-direction: column;
  }

  .home-dashboard__credential-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .home-dashboard__grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .home-dashboard__card-cover {
    height: 220px;
  }

  .home-dashboard__card-body {
    padding: 20px 18px 20px;
  }

  .home-dashboard__card-title {
    min-height: 0;
    font-size: 20px;
  }

  .home-dashboard__meta-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .home-dashboard__watch-button {
    font-size: 15px;
  }

  .home-dashboard__report-button {
    min-height: 48px;
  }
}
</style>
